import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { api, setAccessToken } from "@/lib/api";

// Demo UI gateway + server-issued Bearer token.
// Workspace identity is always derived server-side.
const SessionCtx = createContext(null);

const KEY = "ez_demo_session";

const DEFAULT_EMAIL = "alex@demo.ezisell.app";
const DEFAULT_ORG = "Rivera Sourcing Co. (Demo)";

function readSavedSession() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function authData(response) {
  if (!response || typeof response !== "object") return {};
  return response.data && typeof response.data === "object"
    ? response.data
    : response;
}

export function SessionProvider({ children }) {
  const [session, setSession] = useState(readSavedSession);
  const initialSessionRef = useRef(session);

  // A persisted UI session is not authenticated until we have refreshed
  // its server-issued Bearer token. Keep protected routes unmounted during
  // that refresh so they cannot fire requests with an empty token.
  const [restoring, setRestoring] = useState(Boolean(session));

  useEffect(() => {
    if (session) {
      localStorage.setItem(KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(KEY);
    }
  }, [session]);

  const createServerSession = useCallback(async (email, org) => {
    const em = String(email || DEFAULT_EMAIL).trim();
    const organization = String(org || DEFAULT_ORG).trim();

    // Never let a stale bearer token interfere with session issuance.
    setAccessToken(null);

    const response = await api.authSession(
      em,
      organization
    );

    const data = authData(response);
    const accessToken = data?.access_token || null;

    if (!accessToken) {
      throw new Error("authorization_token_missing");
    }

    const user = data?.user || {};

    setAccessToken(accessToken);

    const nextSession = {
      email: user.email || em,
      name: user.full_name || user.email?.split("@")[0] || "Alex Rivera",
      org: user.org_name || organization,
      role: user.role || "owner",
      plan: user.plan_name || "Growth",
      accessToken,
      workspaceId: user.workspace_id || null,
    };

    setSession(nextSession);

    return nextSession;
  }, []);

  /*
   * A persisted UI session is not proof that its bearer token still exists
   * or is valid. On every fresh application load, issue a fresh server token.
   *
   * This repairs older/stale ez_demo_session records that survived while
   * ezisell.access_token was missing.
   */
  useEffect(() => {
    let active = true;

    const saved = initialSessionRef.current;

    if (!saved) {
      setAccessToken(null);
      setRestoring(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        const em = saved.email || DEFAULT_EMAIL;
        const organization = saved.org || DEFAULT_ORG;

        // Avoid sending an expired bearer token to the session endpoint.
        setAccessToken(null);

        const response = await api.authSession(
          em,
          organization
        );

        const data = authData(response);
        const accessToken = data?.access_token || null;

        if (!accessToken) {
          throw new Error("authorization_token_missing");
        }

        if (!active) return;

        const user = data?.user || {};

        setAccessToken(accessToken);

        setSession((current) => {
          if (!current) return current;

          return {
            ...current,
            email: user.email || em,
            org: user.org_name || organization,
            accessToken,
            workspaceId:
              user.workspace_id ||
              current.workspaceId ||
              null,
          };
        });
      } catch {
        if (!active) return;

        setAccessToken(null);
        setSession(null);
      } finally {
        if (active) {
          setRestoring(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const signIn = useCallback(
    async (email) => {
      try {
        await createServerSession(
          email || DEFAULT_EMAIL,
          DEFAULT_ORG
        );

        return true;
      } catch {
        // Failed server authentication must never create
        // a logged-in-looking client session.
        setAccessToken(null);
        setSession(null);

        return false;
      }
    },
    [createServerSession]
  );

  const signOut = useCallback(() => {
    setAccessToken(null);
    setSession(null);
  }, []);

  return (
    <SessionCtx.Provider
      value={{
        session,
        signIn,
        signOut,
        restoring,
      }}
    >
      {restoring ? (
        <div
          role="status"
          aria-label="Restoring session"
          className="min-h-screen bg-background"
        />
      ) : (
        children
      )}
    </SessionCtx.Provider>
  );
}

export const useSession = () => useContext(SessionCtx);
